/**
 * Staff-only `integration_status_snapshot()` — the one source of truth for
 * "is M365 (or any other integration) actually doing anything".
 *
 * The M365 section is derived purely from rows in `estimates`: it reports
 * `live` once at least one sent estimate carries a Graph message id and
 * `not_configured` otherwise. It does not probe credentials, so a project
 * whose Graph secrets are set but which has never sent still reads
 * `not_configured`. Copy that renders this state must say so.
 */
import { useQuery } from '@tanstack/react-query';
import { db } from '../lib/db';

export type IntegrationState =
  | 'live'
  | 'stale'
  | 'offline'
  | 'not_configured'
  | 'unmapped'
  | 'never_synced'
  | 'last_run_failed'
  | 'never_run'
  | 'never_used'
  | 'unsigned'
  | 'ok'
  | string;

export interface M365Status {
  estimates_sent: number;
  with_message_id: number;
  state: IntegrationState;
}

export interface IntegrationSnapshot {
  m365?: M365Status;
  auvik?: { state: IntegrationState };
  unifi?: { state: IntegrationState; sites_latest_at?: string | null };
  zoho?: { state: IntegrationState; customers_synced?: number };
  audit_log?: { state: IntegrationState };
  health_scoring?: { state: IntegrationState };
  generated_at?: string;
}

export const INTEGRATION_STATUS_QUERY_KEY = ['integration-status'] as const;

export function useIntegrationStatus() {
  return useQuery({
    queryKey: INTEGRATION_STATUS_QUERY_KEY,
    queryFn: async (): Promise<IntegrationSnapshot> => {
      const { data, error } = await db.rpc('integration_status_snapshot', {});
      if (error) throw new Error(error.message || 'integration_status_snapshot failed');
      return (data ?? {}) as IntegrationSnapshot;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Maps a snapshot state onto the StatusBadge dictionary. */
export function integrationBadgeStatus(state: IntegrationState | undefined): string {
  switch (state) {
    case 'live':
    case 'ok':
      return state;
    case 'stale':
    case 'unsigned':
      return 'stale';
    case 'offline':
    case 'last_run_failed':
      return 'offline';
    default:
      return 'unconfigured';
  }
}

export function m365StateLabel(state: IntegrationState | undefined): string {
  switch (state) {
    case 'live':
      return 'Live';
    case 'not_configured':
      return 'Not configured';
    case undefined:
      return 'Unknown';
    default:
      return state.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  }
}
